import { Box } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import type { OpenShiftRequest } from "../../../../utils/models";
import { countNurseResponses } from "../../../../utils/utilFunctions";
import { useUpdateRequestFlatBonus } from "../../../RequestData/hooks/useUpdateRequestFlatBonus";
import { InlineHint } from "../../../UIToolkit/molecules/InlineHint";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import { currentRequestBonus } from "../gapsPickupBonuses";
import { GapsPostIncentiveField } from "./GapsPostIncentiveField";
import { GapsPostedStep } from "./gapsPostedSteps";
import { GapsPostIncentive, postIncentiveOf } from "./useGapsPostShiftForm";

type Props = {
	request: OpenShiftRequest;
	/** `Nurse 7a-7p · Thu 1 Oct` — the shift the incentive is on. */
	context: string;
	onStep: (step: GapsPostedStep) => void;
};

const SHEET = "gaps-posted-incentive";

/**
 * Change a posted shift's pick-up incentive (P3-10): nothing, or a flat amount per person — the post sheet's own field,
 * written through `useUpdateRequestFlatBonus`, which also offers to merge the post into an identical one it now matches.
 *
 * Only a FLAT incentive is edited here: a decay curve (an amount that steps over time) is refused by the backend
 * (`incentive.bonus.cannot.edit.decay`), so the posted sheet never opens this step for one. The note under the field
 * says what the change does and does not do: nobody is notified again, and whoever is approved next is written the new
 * amount (`approveAddRequest` reads the request's current bonus at approval).
 */
export const GapsPostedIncentiveStep: FC<Props> = ({ request, context, onStep }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { updateFlatBonus } = useUpdateRequestFlatBonus();
	const current = currentRequestBonus(request);
	const [incentive, setIncentive] = useState<GapsPostIncentive>(current > 0 ? GapsPostIncentive.Flat : GapsPostIncentive.None);
	const [bonusText, setBonusText] = useState<string>(current > 0 ? String(current) : "");
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const { bonus, isBonusValid } = postIncentiveOf(incentive, bonusText);
	const isChanged = bonus !== current;
	const responseCount = countNurseResponses(request);

	const properties = useMemo(
		() => ({ department_id: chosenDepartment.department_id, request_id: request.id, from_amount: current, to_amount: bonus }),
		[chosenDepartment.department_id, request.id, current, bonus],
	);

	const handleSave = useCallback(async (): Promise<void> => {
		track("gaps_posted_incentive_start", properties);
		setIsSaving(true);
		// `onDone` is the hook's success path (a write, a merge, or a no-op it skipped); an error shows its own snackbar and
		// never calls it. An object, not a `let`: TypeScript cannot see a write made inside the callback.
		const result = { saved: false };
		await updateFlatBonus({
			request,
			bonusAmount: bonus,
			onDone: () => {
				result.saved = true;
			},
		});
		setIsSaving(false);
		if (!result.saved) {
			trackError("gaps_posted_incentive_error", properties);
			return;
		}
		track("gaps_posted_incentive_success", properties);
		onStep(GapsPostedStep.Actions);
	}, [bonus, onStep, properties, request, track, trackError, updateFlatBonus]);

	return (
		<SheetStep
			stepKey={GapsPostedStep.Incentive}
			dataTestId={SHEET}
			title={t("gaps.postedSheet.incentiveTitle")}
			subtitle={context}
			footer={
				<>
					<SheetStepButton tone="dismiss" onClick={() => onStep(GapsPostedStep.Actions)} disabled={isSaving} dataTestId={`${SHEET}-cancel`}>
						{t("back")}
					</SheetStepButton>
					<SheetStepButton
						tone="confirm"
						onClick={() => void handleSave()}
						disabled={!isChanged || !isBonusValid}
						loading={isSaving}
						dataTestId={`${SHEET}-confirm`}
					>
						{t("gaps.postedSheet.incentiveSave")}
					</SheetStepButton>
				</>
			}
		>
			<Box sx={{ px: 2, pt: 2, pb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
				<GapsPostIncentiveField
					incentive={incentive}
					onIncentiveChange={setIncentive}
					bonusText={bonusText}
					onBonusTextChange={setBonusText}
					bonus={bonus}
					isBonusValid={isBonusValid}
					neededNurses={request.needed_nurses}
					testIdPrefix={SHEET}
				/>
				<InlineHint tone="info" dataTestId={`${SHEET}-note`}>
					{responseCount > 0 ? t("gaps.postedSheet.incentiveNoteAnswered", { count: responseCount }) : t("gaps.postedSheet.incentiveNote")}
				</InlineHint>
			</Box>
		</SheetStep>
	);
};
